import type { Booking, BookingStatus, Resource, WorkingHoursProfile } from '@/types';

export const UNALLOCATED_RESOURCE_ID = 'unallocated';

export const UNALLOCATED_RESOURCE: Resource = {
  id: UNALLOCATED_RESOURCE_ID,
  name: 'Unallocated',
  role: '',
  status: 'available',
  type: 'permanent',
  department: 'System',
  schedulerGroup: 'System',
};

export function getResourcePickerOptions(resources: Resource[], isLive: boolean): Resource[] {
  if (!isLive || resources.some((resource) => String(resource.id) === UNALLOCATED_RESOURCE_ID)) {
    return resources;
  }

  return [UNALLOCATED_RESOURCE, ...resources];
}

export function filterResourcesByWorkingDays(
  resources: Resource[],
  startDate: Date | undefined,
  endDate: Date | undefined,
  globalProfile: WorkingHoursProfile | undefined,
  selectedResourceIds: string[],
): Resource[] {
  if (!startDate || !endDate || !globalProfile) return resources;

  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

  return resources.filter((resource) => {
    if (String(resource.id) === UNALLOCATED_RESOURCE_ID) return true;
    if (selectedResourceIds.includes(String(resource.id))) return true;

    const current = new Date(startDate);
    current.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    while (current <= end) {
      const dayKey = dayNames[current.getDay()];
      const profile = resource.useCustomWorkingProfile && resource.customWorkingProfile
        ? resource.customWorkingProfile
        : globalProfile;
      if ((Number(profile[dayKey]) || 0) === 0) return false;
      current.setDate(current.getDate() + 1);
    }

    return true;
  });
}

export function normalizeResourceSelection(nextIds: string[], previousIds: string[]): string[] {
  if (!nextIds.includes(UNALLOCATED_RESOURCE_ID)) return nextIds;
  if (!previousIds.includes(UNALLOCATED_RESOURCE_ID)) return [UNALLOCATED_RESOURCE_ID];

  const allocatedIds = nextIds.filter((id) => id !== UNALLOCATED_RESOURCE_ID);
  return allocatedIds.length > 0 ? allocatedIds : [UNALLOCATED_RESOURCE_ID];
}

export function hasResourceSelection(resourceIds: string[]): boolean {
  return resourceIds.length > 0;
}

export function isUnallocatedSelection(resourceIds: string[]): boolean {
  return resourceIds.includes(UNALLOCATED_RESOURCE_ID);
}

export function getBookingStatusForResourceSelection(
  resourceIds: string[],
  status: BookingStatus,
): BookingStatus {
  return isUnallocatedSelection(resourceIds) ? 'planned' : status;
}

export function isUnallocatedBooking(
  booking: Pick<Booking, 'resourceId'>,
  isLive: boolean,
): boolean {
  return isLive
    ? booking.resourceId == null
    : booking.resourceId === UNALLOCATED_RESOURCE_ID;
}

export function getBookingResourceFields(
  resourceIds: string[],
): Pick<Booking, 'resourceId' | 'resourceIds'> {
  if (isUnallocatedSelection(resourceIds)) {
    return { resourceId: null, resourceIds: undefined };
  }

  return {
    resourceId: resourceIds[0] ?? null,
    resourceIds: resourceIds.length > 0 ? resourceIds : undefined,
  };
}
